import type { PlacedLoot } from "./types";

/**
 * The pure deck-loading rules. The same physical laws the solver's MILP
 * encodes (see @jpjj/solver-core packing3dModel), expressed incrementally for
 * interactive play; board.test.ts proves the two accept exactly the same
 * configurations.
 *
 * A box dropped at (x, y) falls until it lands. It may rest there only if:
 *  - it lies fully inside the roped-off grid,
 *  - the floor under it is LEVEL — every footprint tile at the same height
 *    (no empty space beneath any single tile: no overhangs, no bridges),
 *  - whatever it rests on can bear weight (`stackable` — a barrel top, not
 *    a chest's domed lid),
 *  - its top stays under the height limit (the bosun must see past the pile).
 */

export interface DeckState {
  cols: number;
  rows: number;
  maxH: number;
  placed: PlacedLoot[];
}

export type LandingFault = "bounds" | "uneven" | "fragile" | "height";

export interface Landing {
  ok: boolean;
  /** The layer the box would rest at (its bottom). */
  z: number;
  fault?: LandingFault;
}

/** The piece occupying column (x, y) at layer z, if any. */
export function pieceAt(deck: DeckState, x: number, y: number, z: number): PlacedLoot | undefined {
  return deck.placed.find((p) =>
    x >= p.x && x < p.x + p.w && y >= p.y && y < p.y + p.d && z >= p.z && z < p.z + p.h);
}

/** Height of the stack in column (x, y): one above the topmost occupied layer. */
export function columnTop(deck: DeckState, x: number, y: number): number {
  let top = 0;
  for (const p of deck.placed) {
    if (x >= p.x && x < p.x + p.w && y >= p.y && y < p.y + p.d && p.z + p.h > top) top = p.z + p.h;
  }
  return top;
}

/** Where a w×d×h box dropped at (x, y) would land, and whether it may rest there. */
export function landing(deck: DeckState, x: number, y: number, w: number, d: number, h: number): Landing {
  if (x < 0 || y < 0 || x + w > deck.cols || y + d > deck.rows) return { ok: false, z: 0, fault: "bounds" };
  let z = 0;
  for (let dx = 0; dx < w; dx++)
    for (let dy = 0; dy < d; dy++) z = Math.max(z, columnTop(deck, x + dx, y + dy));
  for (let dx = 0; dx < w; dx++) {
    for (let dy = 0; dy < d; dy++) {
      if (columnTop(deck, x + dx, y + dy) !== z) return { ok: false, z, fault: "uneven" };
      if (z > 0 && !pieceAt(deck, x + dx, y + dy, z - 1)!.stackable) return { ok: false, z, fault: "fragile" };
    }
  }
  if (z + h > deck.maxH) return { ok: false, z, fault: "height" };
  return { ok: true, z };
}

/** The pieces resting (at least partly) on top of `p`. */
export function piecesOn(deck: DeckState, p: PlacedLoot): PlacedLoot[] {
  return deck.placed.filter((q) =>
    q.uid !== p.uid && q.z === p.z + p.h &&
    q.x < p.x + p.w && q.x + q.w > p.x && q.y < p.y + p.d && q.y + q.d > p.y);
}

/** A piece may be taken back only when nothing rests on it. */
export function canPickUp(deck: DeckState, p: PlacedLoot): boolean {
  return piecesOn(deck, p).length === 0;
}

/** Total value currently loaded. */
export function deckValue(deck: DeckState): number {
  return deck.placed.reduce((s, p) => s + p.value, 0);
}
